// src/utils/chartUtils.ts

/* ========= Types ========= */
export type TF = "1m" | "15m" | "30m" | "1h" | "2h" | "4h" | "1d";
export type Dir = "long" | "short" | "wait";
export type Candle = { time: number; open: number; high: number; low: number; close: number; volume: number };

/* ========= Consts ========= */
export const TF_MAP: Record<TF, string> = {
  "1m": "1m",
  "15m": "15m",
  "30m": "30m",
  "1h": "1h",
  "2h": "2h",
  "4h": "4h",
  "1d": "1d",
};
export const BINANCE = "https://api.binance.com";
export const WS_BINANCE = "wss://stream.binance.com:9443/ws";

/* ========= Utils ========= */
/** number -> locale string (fallback '-') */
export const n = (v: number | undefined | null, d = 4) =>
  v == null || !Number.isFinite(v) ? "-" : v.toLocaleString(undefined, { maximumFractionDigits: d });

/* ========= EMA ========= */
export const ema = (arr: number[], p: number) => {
  if (!arr.length || p <= 1) return NaN;
  const k = 2 / (p + 1);
  let prev = arr[0];
  for (let i = 1; i < arr.length; i++) prev = arr[i] * k + prev * (1 - k);
  return prev;
};

export const emaSeries = (closes: number[], p: number, times: number[]) => {
  if (!closes.length || times.length !== closes.length) return [];
  const k = 2 / (p + 1);
  const out: { time: number; value: number }[] = [];
  let prev = closes[0];
  out.push({ time: times[0], value: prev });
  for (let i = 1; i < closes.length; i++) {
    prev = closes[i] * k + prev * (1 - k);
    out.push({ time: times[i], value: prev });
  }
  return out;
};

/* ========= ATR ========= */
export const atr = (c: Candle[], p = 14) => {
  if (c.length < 2) return 0;
  const tr: number[] = [];
  for (let i = 1; i < c.length; i++) {
    const a = c[i],
      b = c[i - 1];
    tr.push(Math.max(a.high - a.low, Math.abs(a.high - b.close), Math.abs(a.low - b.close)));
  }
  const m = Math.min(p, tr.length);
  return m ? tr.slice(-m).reduce((s, v) => s + v, 0) / m : 0;
};

/* ========= Support / Resistance ========= */
export const supportResistance = (c: Candle[], look = 60) => {
  const arr = c.slice(-look);
  if (!arr.length) return { sup: NaN, res: NaN, cntS: 0, cntR: 0 };

  const lows = arr.map((x) => x.low);
  const highs = arr.map((x) => x.high);
  const sup = Math.min(...lows);
  const res = Math.max(...highs);

  const eps = ((res - sup) * 0.01) || 1e-8; // 1% 범위(최소값 보호)
  const cntS = lows.filter((l) => Math.abs(l - sup) <= eps).length;
  const cntR = highs.filter((h) => Math.abs(h - res) <= eps).length;

  return { sup, res, cntS, cntR };
};

/* ========= Classic Pivots ========= */
export const classicPivot = (c: Candle[]) => {
  const last = c.at(-2) ?? c.at(-1);
  if (!last) return null;
  const H = last.high,
    L = last.low,
    C = last.close;
  const P = (H + L + C) / 3;
  return { P, R1: 2 * P - L, S1: 2 * P - H, R2: P + (H - L), S2: P - (H - L) };
};

/* ========= Fibonacci Levels (lookback range) ========= */
export const fibLevels = (c: Candle[], look = 200) => {
  const arr = c.slice(-look);
  if (arr.length < 2) return null;
  const hi = Math.max(...arr.map((x) => x.high));
  const lo = Math.min(...arr.map((x) => x.low));
  const d = hi - lo;
  if (d <= 0) return null;
  return {
    "0.236": hi - 0.236 * d,
    "0.382": hi - 0.382 * d,
    "0.5": hi - 0.5 * d,
    "0.618": hi - 0.618 * d,
    "0.786": hi - 0.786 * d,
    hi,
    lo,
  };
};

/* ========= Volatility Regime (as string) ========= */
/** ProPanel이 문자열("high"|"low"|"neutral")을 기대하므로 문자열 반환 */
export const volatilityRegime = (price: number, A: number): "high" | "low" | "neutral" => {
  const r = price > 0 ? A / price : 0;
  if (r < 0.003) return "low";      // ~0.3%
  if (r < 0.012) return "neutral";  // 0.3%~1.2%
  return "high";                    // >1.2%
};

/* ========= Volume Walls ========= */
export const volumeWalls = (c: Candle[], look = 120, k = 5) => {
  const arr = c.slice(-look);
  if (!arr.length) return [] as { price: number; size: number }[];
  // 거래량 상위 k개 바를 벽으로 가정 (size=volume, price=close)
  const sorted = [...arr].sort((a, b) => b.volume - a.volume).slice(0, Math.max(1, k));
  return sorted.map((x) => ({ price: x.close, size: x.volume }));
};

/* ========= Analysis Shape ========= */
export type Analysis = {
  ema50: number;
  ema200: number;
  atr: number;
  sup: number;
  res: number;
  cntS: number;
  cntR: number;
  dir: Dir;
  plan: { entry: number; stop: number; tp1: number; tp2: number; band: [number, number] };
  score: number;
  rr1: number;
  rr2: number;
  piv: ReturnType<typeof classicPivot> | null;
  fib: ReturnType<typeof fibLevels> | null;
  volReg: "high" | "low" | "neutral"; // 문자열 버전 (ProPanel 호환)
  regime: "squeeze" | "expand" | "neutral";
};

/* ========= Confidence ========= */
export const confidence = (score: number, rr: number) => {
  const s = Math.max(0, Math.min(1, Math.abs(score) / 100));
  const r = Math.max(0, Math.min(1, Math.max(0, Math.min(3, rr)) / 3));
  return Math.round((s * 0.6 + r * 0.4) * 100);
};
